import type {
  WebContentFindListLCollectionOutputDto,
  WebContentFindListOutputDto,
  WebContentFindListTagsOutputDto,
  WebContentFindOneCollectionsDeepDto,
  WebContentFindOneOtherTagsDeepDto,
  WebContentFindOneOutputDto,
  WebContentFindOneTagDeepDto,
  WebContentGlobalSearchOutputDto,
  WebContentGlobalSearchSuggestionOutputDto,
  WebContentSetFindOneVodItemDto,
  WebContentSetGetOneInfoDto,
  WebContentSetLiveGetOneOutputDto,
  WebCreatorFindTagsContentItemOutputDto,
  WebCreatorGlobalSearchContentDeepDto,
} from '@bldsoft/ovp-api-types'
import {
  ContentTypeDefault,
  MediaTypes,
  type Content,
  type ContentLanguage,
  type ContentTag,
  type OtherTag,
  type Tag,
} from '@setplex/tria-api'
import { toDate } from '../../tools'
import { formatFromContentDeep, formatListItem } from '../collections/format'
import { format as formatCreator } from '../creators/format'
import { ApiHomeRowMixedType } from '../home/index.h'
import { formatMixedType } from '../tvshows/format'
import type { ApiContent, ApiContentLanguage, ApiContentTag } from './index.h'

const contentRequiredDefaults = (): Pick<
  Content,
  'createDate' | 'updateDate' | 'isFavorite' | 'newOtherTags' | 'contentType'
> => ({
  createDate: new Date(0),
  updateDate: new Date(0),
  isFavorite: false,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
})

const formatTagFromList = ({
  id,
  title,
}: WebContentFindListTagsOutputDto): Tag => ({
  id,
  title,
})

const formatTagFromDeep = ({
  id,
  title,
  description,
  horizontalImage,
  background,
}: WebContentFindOneTagDeepDto): Tag => ({
  id,
  title,
  description,
  horizontalImage,
  background: [...background],
})

const formatDuration = (duration?: number): string | undefined =>
  duration === undefined ? undefined : String(duration)

const formatOtherTagFromDeep = ({
  id,
  title,
}: WebContentFindOneOtherTagsDeepDto): OtherTag => ({
  id,
  title,
  description: '',
  horizontalImage: '',
  verticalImage: '',
})

const formatCollectionFromList = (
  collection: WebContentFindListLCollectionOutputDto
) => formatListItem(collection)

const formatCollectionFromDeep = (
  collection: WebContentFindOneCollectionsDeepDto
) => formatFromContentDeep(collection)

const formatContentSetVodType = (
  type: WebContentSetFindOneVodItemDto['type']
): ContentTypeDefault | undefined => {
  switch (type) {
    case 'content':
      return ContentTypeDefault.movie
    case 'tv_show':
      return ContentTypeDefault.tvshow
    default:
      return undefined
  }
}

export const formatFromFindOne = ({
  id,
  url,
  title,
  description,
  duration,
  userRating,
  ageRating,
  ageRatingReasons,
  poster,
  banner,
  directors,
  stars,
  createDate,
  updateDate,
  creator,
  isFavorite,
  tags,
  collections,
  otherTags,
  newOtherTags,
  productionCountries,
  releaseYear,
  shortDescription,
}: WebContentFindOneOutputDto): Content => ({
  id,
  url,
  title,
  description,
  duration: formatDuration(duration),
  userRating,
  ageRating,
  ageRatingReasons: [...ageRatingReasons],
  poster,
  banner,
  directors,
  stars,
  createDate: toDate(createDate),
  updateDate: toDate(updateDate),
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  tags: tags.length ? tags.map(formatTagFromDeep) : undefined,
  collections: collections.length
    ? collections.map(formatCollectionFromDeep)
    : undefined,
  otherTags: [...otherTags],
  newOtherTags: newOtherTags.map(formatOtherTagFromDeep),
  contentType: MediaTypes.VIDEO,
  productionCountries: productionCountries
    ? [...productionCountries]
    : undefined,
  releaseYear,
  shortDescription,
})

export const formatFromFindList = ({
  id,
  title,
  description,
  duration,
  userRating,
  ageRating,
  poster,
  banner,
  createDate,
  updateDate,
  creator,
  isFavorite,
  tags,
  collections,
  productionCountries,
  releaseYear,
  shortDescription,
}: WebContentFindListOutputDto): Content => ({
  id,
  title,
  description,
  duration: formatDuration(duration),
  userRating,
  ageRating,
  poster,
  banner,
  createDate: toDate(createDate),
  updateDate: toDate(updateDate),
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  tags: tags?.length ? tags.map(formatTagFromList) : undefined,
  collections: collections?.length
    ? collections.map(formatCollectionFromList)
    : undefined,
  otherTags: undefined,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
  productionCountries: productionCountries
    ? [...productionCountries]
    : undefined,
  releaseYear,
  shortDescription,
})

export const formatFromGlobalSearch = ({
  id,
  title,
  description,
  duration,
  userRating,
  ageRating,
  poster,
  banner,
  creator,
  isFavorite,
  collections,
}: WebContentGlobalSearchOutputDto): Content => ({
  ...contentRequiredDefaults(),
  id,
  title,
  description,
  duration: formatDuration(duration),
  userRating,
  ageRating,
  poster,
  banner,
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  collections: collections?.length
    ? collections.map(formatListItem)
    : undefined,
})

export const formatFromGlobalSearchSuggestion = ({
  id,
  title,
  poster,
  banner,
  creator,
}: WebContentGlobalSearchSuggestionOutputDto): Content => ({
  ...contentRequiredDefaults(),
  id,
  title,
  poster,
  banner,
  creator: creator ? formatCreator(creator) : undefined,
})

export const formatFromContentSetVod = (
  item: WebContentSetFindOneVodItemDto
): Content => ({
  id: item.id,
  title: item.title,
  description: item.description,
  shortDescription: item.shortDescription,
  duration: formatDuration(item.duration),
  userRating: item.userRating,
  ageRating: item.ageRating,
  poster: item.poster,
  banner: item.banner,
  createDate: toDate(item.createDate),
  updateDate: toDate(item.updateDate),
  creator: item.creator ? formatCreator(item.creator) : undefined,
  isFavorite: item.isFavorite,
  tags: item.tags?.length ? item.tags.map(formatTagFromList) : undefined,
  collections: item.collections?.length
    ? item.collections.map(formatListItem)
    : undefined,
  otherTags: undefined,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
  productionCountries: item.productionCountries
    ? [...item.productionCountries]
    : undefined,
  releaseYear: item.releaseYear,
  mixedType: formatContentSetVodType(item.type),
})

export const formatFromCreatorTagsContentItem = ({
  id,
  title,
  description,
  duration,
  userRating,
  ageRating,
  poster,
  banner,
  createDate,
  updateDate,
  creator,
  isFavorite,
  collections,
}: WebCreatorFindTagsContentItemOutputDto): Content => ({
  id,
  title,
  description,
  duration: formatDuration(duration),
  userRating,
  ageRating,
  poster,
  banner,
  createDate: toDate(createDate),
  updateDate: toDate(updateDate),
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  collections: collections?.length
    ? collections.map(formatListItem)
    : undefined,
  otherTags: undefined,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
})

export const formatFromCreatorGlobalSearchContent = ({
  id,
  title,
  duration,
  userRating,
  ageRating,
  poster,
  banner,
  isFavorite,
  collections,
  updateDate,
}: WebCreatorGlobalSearchContentDeepDto): Content => ({
  id,
  title,
  duration: formatDuration(duration),
  userRating,
  ageRating,
  poster,
  banner,
  isFavorite,
  collections: collections?.length
    ? collections.map(formatListItem)
    : undefined,
  createDate: new Date(0),
  updateDate: toDate(updateDate),
  otherTags: undefined,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
})

export const formatFromContentSetInfo = ({
  id,
  title,
  description,
  type,
}: WebContentSetGetOneInfoDto & { type?: ApiHomeRowMixedType }): Content => ({
  ...contentRequiredDefaults(),
  id,
  title,
  description,
  mixedType: type ? formatMixedType(type) : undefined,
})

export const formatFromContentSetLive = ({
  id,
  title,
  description,
}: WebContentSetLiveGetOneOutputDto & {
  type?: ApiHomeRowMixedType
}): Content => ({
  ...contentRequiredDefaults(),
  id,
  title,
  description,
})

const isFindOneContent = (
  content: ApiContent
): content is WebContentFindOneOutputDto => 'url' in content

const isFindListContent = (
  content: ApiContent
): content is WebContentFindListOutputDto =>
  'tags' in content &&
  'createDate' in content &&
  !('url' in content) &&
  !('genres' in content)

const isGlobalSearchContent = (
  content: ApiContent
): content is WebContentGlobalSearchOutputDto =>
  'creator' in content &&
  !('url' in content) &&
  !('createDate' in content) &&
  'description' in content &&
  'duration' in content

const isGlobalSearchSuggestionContent = (
  content: ApiContent
): content is WebContentGlobalSearchSuggestionOutputDto =>
  'creator' in content && !('description' in content)

const isCreatorTagsContentItem = (
  content: ApiContent
): content is WebCreatorFindTagsContentItemOutputDto =>
  'creator' in content &&
  'collections' in content &&
  'createDate' in content &&
  !('url' in content) &&
  !('genres' in content) &&
  !('tags' in content)

const isCreatorGlobalSearchContent = (
  content: ApiContent
): content is WebCreatorGlobalSearchContentDeepDto =>
  'updateDate' in content &&
  !('creator' in content) &&
  !('createDate' in content)

const isContentSetVodItem = (
  content: ApiContent
): content is WebContentSetFindOneVodItemDto => 'genres' in content

const isContentSetLive = (
  content: ApiContent
): content is WebContentSetLiveGetOneOutputDto & {
  type?: ApiHomeRowMixedType
} => 'itemsCount' in content

export const format = (content: ApiContent): Content => {
  if (isFindOneContent(content)) {
    return formatFromFindOne(content)
  }

  if (isFindListContent(content)) {
    return formatFromFindList(content)
  }

  if (isCreatorTagsContentItem(content)) {
    return formatFromCreatorTagsContentItem(content)
  }

  if (isCreatorGlobalSearchContent(content)) {
    return formatFromCreatorGlobalSearchContent(content)
  }

  if (isGlobalSearchContent(content)) {
    return formatFromGlobalSearch(content)
  }

  if (isGlobalSearchSuggestionContent(content)) {
    return formatFromGlobalSearchSuggestion(content)
  }

  if (isContentSetVodItem(content)) {
    return formatFromContentSetVod(content)
  }

  if (isContentSetLive(content)) {
    return formatFromContentSetLive(content)
  }

  return formatFromContentSetInfo(content)
}

export const formatContentTag = (
  tag: ApiContentTag | ApiContentLanguage
): ContentTag | ContentLanguage => {
  if ('description' in tag) {
    return {
      id: tag.id,
      title: tag.title || '',
      description: tag.description || '',
      seoDescription: tag.seoDescription || '',
    }
  }

  return {
    id: tag.id,
    title: tag.title || '',
    description: '',
    seoDescription: '',
  }
}
